import React, { useState, useEffect } from "react"
import { Col, Row, Form } from "reactstrap"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import * as Yup from "yup"

import { Modal } from "../../../components/Modal"
import { TextInput } from "../../../components/TextInput"
import { Button } from "../../../components/Button"
import { AutoComplete } from "../../../components/AutoComplete"
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText"
import ActiveInactiveSwitch from "../../Configurator/CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import useAsyncSelect from "../../../hooks/useAsyncSelect"
import autoCompleteStyles from "../../../components/AutoComplete/autoCompleteStyles"
import FormSkeleton from "components/FormSkeleton/FormSkeleton"
import {
   CreateEquipmentCategory,
   GetEquipmentCategoryDetails,
   UpdateEquipmentCategory,
   GetTrailersListing,
} from "../../../api/api.service"
import { EquipmentCategoriesStatus, TrailerSize } from "../../../constants"
import { makeTrailerName } from "utils/commonUtils"

const FIELDS_NAME = {
   NAME: "name",
   STATUS: "status",
   TRAILER_TYPE: "trailerType",
}

const TITLES = {
   MODAL_ADD_TITLE: "Add Equipment",
   MODAL_UPDATE_TITLE: "Update Equipment",
   MODAL_DETAILS_TITLE: "Equipment details",
   ADD_BTN_TEXT: "Save",
   UPDATE_BTN_TEXT: "Update",
   DETAILS_BTN_TEXT: "Done",
}

const handleStatusConversion = status => {
   return status === EquipmentCategoriesStatus.Active
}

const ManageGlobalEquipmentModal = ({
   isDetailView = false,
   isOpen = false,
   onToggleModal = () => {},
   onSuccess = () => {},
   id = "",
}) => {
   const [isLoading, setIsLoading] = useState(false)
   const [status, setStatus] = useState(true)
   const [isFetching, setIsFetching] = useState(false)

   const {
      setSelectedOption: setSelectedTrailerType,
      selectEntity: trailerTypeOptions,
      setSelectEntity: setTrailerTypeOptions,
      handleSearchOnOptions: handleSearchTrailerType,
      selectedOption: selectedTrailerType,
      handleGetSelectEntityData,
   } = useAsyncSelect({
      apiFunc: GetTrailersListing,
      labelKey: "trailerType",
      valueKey: "id",
      params: { limit: -1 },
      modifyLabel: true,
      handleLabelModification: options => {
         return options.map(item => {
            const { description, ...rest } = item
            return {
               ...rest,
               label: makeTrailerName({
                  trailerType: item?.trailerType?.trailerType,
                  trailerModel: item?.model[0],
                  trailerSize: TrailerSize.getDisplayTextKey(
                     item?.configuratorTrailerSizeType[0]
                  ),
               }),
            }
         })
      },
   })

   const handleTrailerTypeChange = selectedOptions => {
      const options = selectedOptions || []
      setSelectedTrailerType(options)
      formik.setFieldValue(FIELDS_NAME.TRAILER_TYPE, options)
   }

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
         [FIELDS_NAME.NAME]: "",
         [FIELDS_NAME.STATUS]: EquipmentCategoriesStatus.Active,
         [FIELDS_NAME.TRAILER_TYPE]: [],
      },
      validationSchema: Yup.object({
         [FIELDS_NAME.NAME]: Yup.string()
            .required("Enter feature name")
            .min(3, "Enter feature name between 3 to 250 characters")
            .max(250, "Enter feature name between 3 to 250 characters"),
         [FIELDS_NAME.STATUS]: Yup.string().required("Select status"),
         // [FIELDS_NAME.TRAILER_TYPE]: Yup.mixed().test(
         //    "trailerType",
         //    "Select at least one trailer type",
         //    value => {
         //       return Array.isArray(value) && value.length > 0
         //    }
         // ),
      }),
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   useEffect(() => {
      if (isOpen) {
         if (id) {
            handleFetchDetails()
         } else {
            formik.resetForm()
            setSelectedTrailerType([])
            setStatus(true)
         }
      }
      return () => {
         setIsLoading(false)
      }
   }, [id, isOpen])

   const handleFetchDetails = async () => {
      if (!id) return

      try {
         setIsFetching(true)

         const res = await GetEquipmentCategoryDetails(id)

         const trailerIds = res?.configuratorTrailerEquipmentSubTypes?.length
            ? res.configuratorTrailerEquipmentSubTypes
                 .map(t => t.configuratorTrailer?.id)
                 .filter(Boolean)
            : []

         let trailerTypesList = trailerTypeOptions?.data || []
         if (trailerTypesList.length === 0) {
            await handleGetSelectEntityData(1)
            trailerTypesList = trailerTypeOptions?.data || []
         }

         const trailerTypesData = trailerIds
            .map(trailerId => {
               const matchedType = trailerTypesList.find(
                  opt => opt.id === trailerId
               )
               return matchedType || null
            })
            .filter(Boolean)

         formik.setValues({
            [FIELDS_NAME.NAME]: res?.name || "",
            [FIELDS_NAME.STATUS]:
               res.status || EquipmentCategoriesStatus.Active,
            [FIELDS_NAME.TRAILER_TYPE]: trailerTypesData,
         })

         setStatus(handleStatusConversion(res.status))

         setSelectedTrailerType(trailerTypesData)
      } catch (error) {
         toast.error("Failed to fetch details")
      } finally {
         setIsFetching(false)
      }
   }

   const handleSubmit = async values => {
      const trailerTypeIds = Array.isArray(values[FIELDS_NAME.TRAILER_TYPE])
         ? values[FIELDS_NAME.TRAILER_TYPE]
              .map(item => {
                 if (typeof item === "object" && item !== null) {
                    return item.value || item.id || null
                 }
                 return item || null
              })
              .filter(id => id !== null && id !== undefined)
         : []

      const bodyData = {
         name: values[FIELDS_NAME.NAME].trim(),
         status: values[FIELDS_NAME.STATUS],
         configuratorTrailerIds: trailerTypeIds,
      }

      try {
         setIsLoading(true)
         if (id) {
            await UpdateEquipmentCategory(id, bodyData)
            toast.success("Equipment category has been updated successfully!")
         } else {
            await CreateEquipmentCategory(bodyData)
            toast.success("Equipment category has been added successfully!")
         }
         handleModalClose()
         onSuccess()
      } catch (error) {
         toast.error(`Failed to ${id ? "update" : "add"} equipment`)
      } finally {
         setIsLoading(false)
      }
   }

   const handleModalClose = () => {
      formik.resetForm()
      setSelectedTrailerType([])
      setStatus(true)
      onToggleModal()
   }

   const handleSetStatus = evt => {
      const { name } = evt.target
      const alteredStatus = !status
      setStatus(alteredStatus)
      formik.setFieldValue(
         name,
         alteredStatus
            ? EquipmentCategoriesStatus.Active
            : EquipmentCategoriesStatus.Inactive
      )
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         bodyClassName=""
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container mb-0"
         size="md"
         backdrop="static"
         modalTitle={
            isDetailView
               ? TITLES.MODAL_DETAILS_TITLE
               : id
               ? TITLES.MODAL_UPDATE_TITLE
               : TITLES.MODAL_ADD_TITLE
         }
         scrollable
      >
         {isFetching ? (
            <FormSkeleton />
         ) : (
            <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
               <div className="d-flex justify-content-center mb-3">
                  <h3 className="m-0 p-0 fw-bold text-blue">Equipment</h3>
               </div>
               <Row>
                  <Col xs={12}>
                     <Form onSubmit={formik.handleSubmit}>
                        <Row className="mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Name
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter name"
                                 name={FIELDS_NAME.NAME}
                                 {...formik.getFieldProps(FIELDS_NAME.NAME)}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.NAME}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Configurator Trailers
                           </label>
                           <div className="col-md-9">
                              <AutoComplete
                                 isDisabled={isDetailView}
                                 handleBlur={() =>
                                    setTrailerTypeOptions(prev => ({
                                       ...prev,
                                       page: 1,
                                       Q: "",
                                    }))
                                 }
                                 onChange={handleTrailerTypeChange}
                                 onInputChange={handleSearchTrailerType}
                                 placeholder="Select Trailer Type"
                                 classNamePrefix="add-new-req"
                                 customStyles={autoCompleteStyles.STYLE_FOUR}
                                 options={trailerTypeOptions?.data}
                                 value={selectedTrailerType}
                                 isLoading={trailerTypeOptions?.fetching}
                                 name={FIELDS_NAME.TRAILER_TYPE}
                                 isMulti={true}
                                 closeMenuOnSelect={false}
                                 menuPortalTarget={document.body}
                                 menuPosition="fixed"
                                 menuPlacement="auto"
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.TRAILER_TYPE}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Status
                           </label>
                           <div className="col-md-9">
                              <ActiveInactiveSwitch
                                 isChecked={status}
                                 isDisable={isDetailView}
                                 name={FIELDS_NAME.STATUS}
                                 onChange={handleSetStatus}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.STATUS}
                              />
                           </div>
                        </Row>

                        <div className="d-flex justify-content-center mt-3">
                           <Button
                              isLoading={isLoading}
                              type={isDetailView ? "button" : "submit"}
                              title={
                                 isDetailView
                                    ? TITLES.DETAILS_BTN_TEXT
                                    : id
                                    ? TITLES.UPDATE_BTN_TEXT
                                    : TITLES.ADD_BTN_TEXT
                              }
                              className="header-button"
                              onClick={
                                 isDetailView ? handleModalClose : undefined
                              }
                           />
                        </div>
                     </Form>
                  </Col>
               </Row>
            </div>
         )}
      </Modal>
   )
}

export default React.memo(ManageGlobalEquipmentModal)
